/**
 * PublicRoute
 *
 * Wraps authentication pages (/login, /forgot-password, etc.).
 * If the user is already authenticated, they are redirected to the dashboard
 * so they don't see the login form unnecessarily.
 *
 * Loading state: renders a spinner while session restoration is in progress
 * to avoid a flash of the login form that immediately redirects.
 */
import { Navigate } from 'react-router-dom'
import { type ReactNode } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ROUTES } from '@/constants/routes'

interface PublicRouteProps {
  children: ReactNode
}

export function PublicRoute({ children }: PublicRouteProps) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" label="Loading…" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to={ROUTES.DASHBOARD} replace />
  }

  return <>{children}</>
}
